// src/Components/GoogleMap/GoogleMap.js

import React, { useRef, useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { loadGoogleMapsApi } from './MapLoader';
import { usePolygonManager } from './PolygonManager';
import { usePolylineManager } from './PolylineManager';
import { useMarkerManager } from './MarkerManager';
import { useMapInitialView } from '../../Hooks/useMapInitialView';
import { useMapData } from '../../Hooks/useMapData';
import { useIcons } from '../../Contexts/IconContext';
import { useMapState } from '../../Contexts/MapContext';

import { useMapFilter } from '../../Hooks/useMapFilter';
import { useMapSearch } from '../../Hooks/useMapSearch';
import MapOverlays from './MapOverlays';
import InteractionLayer from './InteractionLayer';

const originalWarn = console.warn;
console.warn = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('The map is not a vector map')) {
    return; // Suppress
  }
  originalWarn.apply(console, args);
};

const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('The map is not a vector map')) {
    return; // Suppress
  }
  originalError.apply(console, args);
};

const DEFAULT_POLYGON_COLOR = '#FF9900';
const DEFAULT_LINE_COLOR = '#00B0FF';
const DEFAULT_LINE_WEIGHT = 4;

export default function GoogleMap({
  activeMenu,
  activeTool,
  setActiveTool,
  currentAction,
  drawingStyles,
  setDrawingStyles,
  style,
  setSelectedMarkerData,
  isDrawerEditing,
  hasUnsavedChanges,
  onSelectionBlocked,
  selectedMarker,
  setGetMapCurrentState,
  onMapLoad,
  filterCriteria,
  onFilterResults,
  searchMarker,
  onClearSearchMarker,
}) {
  const [mapContainer, setMapContainer] = useState(null);
  const mapInstance = useRef(null);
  const projectionRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [markerCounts, setMarkerCounts] = useState({
    displayed: 0,
    totalInView: 0,
    total: 0,
  });

  // eslint-disable-next-line no-unused-vars
  const [portalTarget, setPortalTarget] = useState(null);

  const { selectedFeature, resetMap } = useMapState();
  const hiddenMarkerId = selectedFeature?.id || null;

  const isDrawerEditingRef = useRef(isDrawerEditing);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const interactionLockRef = useRef(false);
  const globalLockRef = useRef(false);
  const internalActionLock = useRef(null);

  const drawingStylesRef = useRef(drawingStyles);
  useEffect(() => {
    drawingStylesRef.current = drawingStyles;
  }, [drawingStyles]);

  const prevActivePolygonIdRef = useRef(null);
  const prevActivePolylineIdRef = useRef(null);
  const prevActiveMarkerIdRef = useRef(null);
  const prevMenuRef = useRef(activeMenu);

  const prevAppliedStylesRef = useRef({ styles: {}, ids: {} });

  const cleanupRefs = useRef({
    clearPolygon: null,
    clearPolyline: null,
    deselectMarker: null,
  });

  const { defaultIcon, defaultIconID } = useIcons();

  const { viewConfig } = useMapInitialView();
  const {
    markersData,
    setMarkersData,
    polygonsData,
    setPolygonsData,
    polylinesData,
    setPolylinesData,
  } = useMapData();

  const { filteredMarkers, filteredPolygons, filteredPolylines } = useMapFilter(
    markersData,
    polygonsData,
    polylinesData,
    filterCriteria,
    onFilterResults,
  );

  useMapSearch(mapInstance, searchMarker, isMapLoaded, setSelectedMarkerData);

  const setInteractionLock = useCallback((locked) => {
    interactionLockRef.current = locked;
  }, []);

  const tooltipLockRef = useRef(false);

  const handleSetTooltip = useCallback((val) => {
    if (tooltipLockRef.current && val !== null) {
      return;
    }

    if (typeof val === 'function') {
      setTooltip((prev) => {
        const newVal = val(prev);
        return newVal;
      });
    } else {
      setTooltip(val);
    }
  }, []);

  const triggerTooltipLockout = useCallback(() => {
    setTooltip(null);
    tooltipLockRef.current = true;

    setTimeout(() => {
      setTooltip(null);
    }, 50);

    setTimeout(() => {
      tooltipLockRef.current = false;
    }, 500);
  }, []);

  useEffect(() => {
    isDrawerEditingRef.current = isDrawerEditing;
  }, [isDrawerEditing]);

  hasUnsavedChangesRef.current = hasUnsavedChanges;

  useEffect(() => {
    if (isMapLoaded && mapInstance.current && setGetMapCurrentState) {
      setGetMapCurrentState(() => () => {
        const center = mapInstance.current.getCenter();
        const zoom = mapInstance.current.getZoom();
        return {
          lat: center ? center.lat() : 0,
          lng: center ? center.lng() : 0,
          zoom: zoom || 0,
        };
      });
    }
  }, [isMapLoaded, setGetMapCurrentState]);

  const onDebugUpdate = useCallback((info) => {}, []);

  const activeIdsRef = useRef({ poly: null, line: null, marker: null });

  const clearAllExcept = useCallback(
    (keepType, pendingData = null) => {
      if (hasUnsavedChangesRef.current) {
        if (currentAction?.type === 'FORCE_SELECT') {
        } else {
          let currentGroupId = selectedMarker?.markerGroupID || selectedMarker?.id;

          if (!currentGroupId) {
            if (activeIdsRef.current.poly) {
              const p = polygonsData.find((x) => x.id === activeIdsRef.current.poly);
              currentGroupId = p?.markerGroupID || p?.id;
            } else if (activeIdsRef.current.line) {
              const l = polylinesData.find((x) => x.id === activeIdsRef.current.line);
              currentGroupId = l?.markerGroupID || l?.id;
            } else if (activeIdsRef.current.marker) {
              const m = markersData.find((x) => x.id === activeIdsRef.current.marker);
              currentGroupId = m?.markerGroupID || m?.id;
            }
          }

          let pendingGroupId = pendingData?.markerGroupID;
          if (!pendingGroupId) {
            if (pendingData?.isNew) pendingGroupId = currentGroupId;
            else pendingGroupId = pendingData?.id;
          }

          const isDraftingNew = currentAction?.type === 'ADD_NEW';

          const isSameGroup =
            (currentGroupId &&
              pendingGroupId &&
              String(currentGroupId) === String(pendingGroupId)) ||
            isDraftingNew;

          if (!isSameGroup) {
            if (onSelectionBlocked) onSelectionBlocked(pendingData);
            return false;
          }
        }
      }

      if (keepType !== 'POLYGON' && cleanupRefs.current.clearPolygon)
        cleanupRefs.current.clearPolygon(true, false);
      if (keepType !== 'LINE' && cleanupRefs.current.clearPolyline)
        cleanupRefs.current.clearPolyline(true, false);
      if (keepType !== 'MARKER' && cleanupRefs.current.deselectMarker)
        cleanupRefs.current.deselectMarker(null, false);
      return true;
    },
    [
      onSelectionBlocked,
      selectedMarker,
      currentAction,
      polygonsData,
      polylinesData,
      markersData,
    ],
  );

  const [polygonSelectionMode, setPolygonSelectionMode] = useState(false);
  const [lineSelectionMode, setLineSelectionMode] = useState(false);
  const [markerSelectionMode, setMarkerSelectionMode] = useState(false);

  const isDrawMode = currentAction?.type === 'ADD_NEW';

  const handleSetSelectedMarkerData = useCallback(
    (data) => {
      if (!data) {
        if (setSelectedMarkerData) setSelectedMarkerData(null);
        return;
      }

      const resolveSiblings = (dataObj) => {
        let activeDraftGroupId = null;
        if (currentAction?.type === 'ADD_NEW' && currentAction.payload?.markerGroupID) {
          activeDraftGroupId = currentAction.payload.markerGroupID;
        } else if (selectedMarker?.markerGroupID) {
          activeDraftGroupId = selectedMarker.markerGroupID;
        }

        const groupIdStr = dataObj.markerGroupID
          ? String(dataObj.markerGroupID)
          : activeDraftGroupId
            ? String(activeDraftGroupId)
            : String(dataObj.id);

        const t = (dataObj.type || '').toLowerCase();

        const sibM = markersData.find(
          (m) =>
            (m.markerGroupID && String(m.markerGroupID) === groupIdStr) ||
            String(m.id) === groupIdStr,
        );
        const sibP = polygonsData.find(
          (p) =>
            (p.markerGroupID && String(p.markerGroupID) === groupIdStr) ||
            String(p.id) === groupIdStr,
        );
        const sibL = polylinesData.find(
          (l) =>
            (l.markerGroupID && String(l.markerGroupID) === groupIdStr) ||
            String(l.id) === groupIdStr,
        );

        const finalMarkerID =
          t === 'marker' && dataObj.id
            ? dataObj.id
            : sibM?.id || dataObj.markerID || undefined;
        const finalPolyID =
          t === 'polygon' && dataObj.id
            ? dataObj.id
            : sibP?.id || dataObj.polygonID || undefined;
        const finalLineID =
          (t === 'line' || t === 'polyline') && dataObj.id
            ? dataObj.id
            : sibL?.id || dataObj.polylineID || undefined;

        const resolved = { ...dataObj, markerGroupID: groupIdStr };

        if (finalMarkerID) resolved.markerID = finalMarkerID;
        if (finalPolyID) resolved.polygonID = finalPolyID;
        if (finalLineID) resolved.polylineID = finalLineID;

        resolved.groupHasMarker = !!finalMarkerID;
        resolved.groupHasPolygon = !!finalPolyID;
        resolved.groupHasPolyline = !!finalLineID;

        return resolved;
      };

      if (typeof data === 'function') {
        if (setSelectedMarkerData) {
          setSelectedMarkerData((prev) => {
            const resolvedData = data(prev);
            if (!resolvedData) return null;
            return resolveSiblings(resolvedData);
          });
        }
        return;
      }

      if (setSelectedMarkerData) {
        setSelectedMarkerData(resolveSiblings(data));
      }
    },
    [
      setSelectedMarkerData,
      markersData,
      polygonsData,
      polylinesData,
      currentAction,
      selectedMarker,
    ],
  );

  const {
    isPlacingMarker,
    isEditingMarker,
    isPlacingMarkerRef,
    isRenderingMarkers,
    startNewMarker,
    loadMarkers,
    handleMapClickForMarker,
    saveMarker,
    saveMarkerEdits,
    cancelMarker,
    cancelMarkerEdit,
    deleteMarker,
    deselectViewMarker,
    changeMarkerIcon,
    handleMarkerSelect,
    activeMarkerId,
    setMarkersClickable,
  } = useMarkerManager(
    mapInstance,
    filteredMarkers,
    setMarkersData,
    markerSelectionMode,
    handleSetTooltip,
    handleSetSelectedMarkerData,
    isDrawerEditing,
    globalLockRef,
    setIsSaving,
    activeMenu,
    drawingStyles,
    clearAllExcept,
    setInteractionLock,
    setDrawingStyles,
    hiddenMarkerId,
    selectedMarker,
    isDrawMode,
    interactionLockRef,
    activeTool,
    setMarkerCounts,
  );

  const {
    isEditingPolygon,
    activePolygonId,
    isDrawing,
    isDrawingRef,
    loadPolygons,
    saveEdits,
    deletePolygon,
    cancelEdit,
    changePolygonColor,
    clearActivePolygon,
    startDrawing,
    cancelDrawing,
    handleDrawClick,
    handleMouseMove,
    completePolygon,
    enablePolyEditing,
    setPolygonsClickable,
  } = usePolygonManager(
    mapInstance,
    filteredPolygons,
    setPolygonsData,
    polygonSelectionMode,
    handleSetTooltip,
    onDebugUpdate,
    handleSetSelectedMarkerData,
    globalLockRef,
    setIsSaving,
    activeMenu,
    clearAllExcept,
    setInteractionLock,
    isDrawMode,
    interactionLockRef,
    activeTool,
    drawingStyles,
  );

  const {
    isEditingPolyline,
    activePolylineId,
    isDrawingLine,
    isDrawingLineRef,
    loadPolylines,
    startDrawingLine,
    cancelDrawingLine,
    handleLineDrawClick,
    handleLineMouseMove,
    completePolyline,
    saveLineEdits,
    deletePolyline,
    cancelLineEdit,
    changePolylineColor,
    changePolylineWeight,
    changePolylineDash,
    clearActivePolyline,
    enterLineEditMode,
    setPolylinesClickable,
  } = usePolylineManager(
    mapInstance,
    filteredPolylines,
    setPolylinesData,
    lineSelectionMode,
    handleSetTooltip,
    onDebugUpdate,
    handleSetSelectedMarkerData,
    globalLockRef,
    setIsSaving,
    activeMenu,
    clearAllExcept,
    setInteractionLock,
    setDrawingStyles,
    isDrawMode,
    interactionLockRef,
    activeTool,
    drawingStyles,
  );

  useEffect(() => {
    activeIdsRef.current = {
      poly: activePolygonId,
      line: activePolylineId,
      marker: activeMarkerId,
    };
  }, [activePolygonId, activePolylineId, activeMarkerId]);

  const isCurrentlyDrawing = isPlacingMarker || isDrawing || isDrawingLine;
  const isCurrentlyEditing = isEditingPolygon || isEditingPolyline || isEditingMarker;
  const isCurrentlyEditingShape = isEditingPolygon || isEditingPolyline;

  const forceDeselectAll = useCallback(
    (reason = 'Unknown') => {
      if (typeof clearActivePolygon === 'function') clearActivePolygon(true, true);
      if (typeof clearActivePolyline === 'function') clearActivePolyline(true, true);
      if (typeof deselectViewMarker === 'function') deselectViewMarker(null, true);

      setPolygonSelectionMode(false);
      setMarkerSelectionMode(false);
      setLineSelectionMode(false);

      prevActivePolygonIdRef.current = null;
      prevActivePolylineIdRef.current = null;
      prevActiveMarkerIdRef.current = null;

      triggerTooltipLockout();
    },
    [clearActivePolygon, clearActivePolyline, deselectViewMarker, triggerTooltipLockout],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeMenu === 'HOME') {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        if (document.querySelector('.MuiDialog-root')) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        setTimeout(() => {
          if (
            interactionLockRef.current ||
            window.__shapeClickLock ||
            window.__vertexInteractionLock
          ) {
            return;
          }

          const isLocalDrawing =
            isPlacingMarkerRef.current ||
            isDrawingRef.current ||
            isDrawingLineRef.current;

          if (hasUnsavedChangesRef.current && !isLocalDrawing) {
            if (onSelectionBlocked) onSelectionBlocked();
            return;
          }

          if (isLocalDrawing) return;

          forceDeselectAll('Escape Key Click-Away');
          if (resetMap) resetMap();
          if (onClearSearchMarker) onClearSearchMarker();
          if (setSelectedMarkerData) setSelectedMarkerData(null);
        }, 50);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [
    activeMenu,
    forceDeselectAll,
    resetMap,
    onClearSearchMarker,
    setSelectedMarkerData,
    onSelectionBlocked,
    isPlacingMarkerRef,
    isDrawingRef,
    isDrawingLineRef,
  ]);

  useEffect(() => {
    const makeClickable = !isCurrentlyDrawing;

    const timer = setTimeout(() => {
      if (setMarkersClickable) setMarkersClickable(makeClickable);
      if (setPolygonsClickable) setPolygonsClickable(makeClickable);
      if (setPolylinesClickable) setPolylinesClickable(makeClickable);
    }, 10);

    return () => clearTimeout(timer);
  }, [
    isCurrentlyDrawing,
    setMarkersClickable,
    setPolygonsClickable,
    setPolylinesClickable,
  ]);

  useEffect(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    if (isCurrentlyDrawing) {
      mapInstance.current.setOptions({
        draggableCursor: 'crosshair',
        draggingCursor: 'crosshair',
        gestureHandling: 'none',
        clickableIcons: false,
      });
    } else if (isCurrentlyEditing) {
      mapInstance.current.setOptions({
        draggableCursor: 'grab',
        draggingCursor: 'grabbing',
        gestureHandling: 'greedy',
        clickableIcons: false,
      });
    } else if (isDrawMode) {
      mapInstance.current.setOptions({
        draggableCursor: 'crosshair',
        draggingCursor: 'crosshair',
        gestureHandling: 'none',
        clickableIcons: false,
      });
    } else {
      mapInstance.current.setOptions({
        draggableCursor: 'grab',
        draggingCursor: 'grabbing',
        gestureHandling: 'greedy',
        clickableIcons: true,
      });
    }
  }, [isMapLoaded, isDrawMode, isCurrentlyDrawing, isCurrentlyEditing]);

  useEffect(() => {
    if (!mapInstance.current) return;

    const interval = setInterval(() => {
      if (!isCurrentlyDrawing && !isCurrentlyEditingShape) return;

      const mapDiv = mapInstance.current.getDiv();
      if (!mapDiv) return;

      const elements = [mapDiv, ...mapDiv.querySelectorAll('*')];

      if (isCurrentlyDrawing) {
        elements.forEach((el) => {
          const inlineCursor = el.style.getPropertyValue('cursor');
          if (inlineCursor === 'grab' || inlineCursor === 'pointer') {
            el.style.removeProperty('cursor');
          }
        });
        return;
      }

      if (!isCurrentlyEditingShape) return;

      elements.forEach((el) => {
        const inlineCursor = el.style.getPropertyValue('cursor');

        if (inlineCursor === 'crosshair') {
          el.style.setProperty('cursor', 'pointer');
        }
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isCurrentlyEditingShape, isCurrentlyDrawing]);

  useEffect(() => {
    cleanupRefs.current.clearPolygon = clearActivePolygon;
    cleanupRefs.current.clearPolyline = clearActivePolyline;
    cleanupRefs.current.deselectMarker = deselectViewMarker;
  }, [clearActivePolygon, clearActivePolyline, deselectViewMarker]);

  useEffect(() => {
    if (window.google && isMapLoaded) {
      loadMarkers(window.google);
    }
  }, [filteredMarkers, isMapLoaded, loadMarkers, activeMarkerId]);

  useEffect(() => {
    if (!isMapLoaded || !mapInstance.current || !window.google) return;

    const listener = window.google.maps.event.addListener(
      mapInstance.current,
      'idle',
      () => {
        loadMarkers(window.google);
      },
    );

    return () => window.google.maps.event.removeListener(listener);
  }, [isMapLoaded, loadMarkers]);

  useEffect(() => {
    if (window.google && isMapLoaded) loadPolygons(window.google);
  }, [filteredPolygons, isMapLoaded, loadPolygons]);

  useEffect(() => {
    if (window.google && isMapLoaded) loadPolylines(window.google);
  }, [filteredPolylines, isMapLoaded, loadPolylines]);

  useEffect(() => {
    if (activeMenu === 'HOME') {
      if (isEditingPolygon) globalLockRef.current = 'POLYGON';
      else if (isEditingPolyline) globalLockRef.current = 'LINE';
      else if (isEditingMarker) globalLockRef.current = 'MARKER';
      else globalLockRef.current = null;
    } else
      globalLockRef.current = isEditingPolygon || isEditingPolyline || isEditingMarker;
  }, [isEditingPolygon, isEditingPolyline, isEditingMarker, activeMenu]);

  useEffect(() => {
    if (activeMenu !== prevMenuRef.current) {
      prevMenuRef.current = activeMenu;
      if (['MARKUP_MARKERS', 'HOME', 'VIEW_INFO', 'MARKUP_INFO'].includes(activeMenu)) {
        if (currentAction?.type !== 'EDIT' && currentAction?.type !== 'ADD_NEW') {
          forceDeselectAll('activeMenu changed');
          if (setSelectedMarkerData) setSelectedMarkerData(null);
        }
      }
    }
  }, [activeMenu, forceDeselectAll, setSelectedMarkerData, currentAction]);

  useEffect(() => {
    const safeString = (val) => (typeof val === 'string' ? val.toLowerCase() : '');

    const incomingStyles = {
      icon: drawingStyles.markerIcon,
      iconID: drawingStyles.iconID,
      polyColor: safeString(drawingStyles.polygonColor),
      lineColor: safeString(drawingStyles.lineColor),
      lineWeight: drawingStyles.lineWeight,
      isDashed: drawingStyles.isDashed || false,
      dashColor: safeString(drawingStyles.dashColor || '#000000'),
      dashLength: drawingStyles.dashLength || 10,
    };

    const activeIds = {
      poly: activePolygonId,
      line: activePolylineId,
      marker: activeMarkerId,
    };

    const prevData = prevAppliedStylesRef.current || { styles: {}, ids: {} };
    const prevStyles = prevData.styles;
    const prevIds = prevData.ids;

    const stylesChanged = JSON.stringify(prevStyles) !== JSON.stringify(incomingStyles);
    const idsChanged = JSON.stringify(prevIds) !== JSON.stringify(activeIds);

    if (!stylesChanged && !idsChanged) return;

    prevAppliedStylesRef.current = { styles: incomingStyles, ids: activeIds };

    if (activeTool === 'POLYGON' && activePolygonId) {
      if (prevStyles.polyColor !== incomingStyles.polyColor) {
        if (changePolygonColor && !interactionLockRef.current) {
          changePolygonColor(drawingStyles.polygonColor || DEFAULT_POLYGON_COLOR);
        }
      }
    }

    if (activeTool === 'MARKER' && activeMarkerId) {
      if (
        prevStyles.icon !== incomingStyles.icon ||
        prevStyles.iconID !== incomingStyles.iconID
      ) {
        const isExisting = markersData.some((m) => m.id === activeMarkerId);
        let styleIcon =
          drawingStyles.markerIcon === 'MarkerDisplay.svg'
            ? null
            : drawingStyles.markerIcon;
        if (
          (isExisting || styleIcon || drawingStyles.iconID) &&
          !interactionLockRef.current
        ) {
          if (changeMarkerIcon) changeMarkerIcon(styleIcon, drawingStyles.iconID);
        }
      }
    }

    if (activeTool === 'LINE' && activePolylineId) {
      if (prevStyles.lineColor !== incomingStyles.lineColor) {
        if (changePolylineColor && !interactionLockRef.current)
          changePolylineColor(drawingStyles.lineColor || DEFAULT_LINE_COLOR);
      }
      if (prevStyles.lineWeight !== incomingStyles.lineWeight) {
        if (changePolylineWeight && !interactionLockRef.current)
          changePolylineWeight(drawingStyles.lineWeight || DEFAULT_LINE_WEIGHT);
      }
      if (
        prevStyles.isDashed !== incomingStyles.isDashed ||
        prevStyles.dashColor !== incomingStyles.dashColor ||
        prevStyles.dashLength !== incomingStyles.dashLength
      ) {
        if (changePolylineDash && !interactionLockRef.current) {
          changePolylineDash(
            incomingStyles.isDashed,
            incomingStyles.dashColor,
            incomingStyles.dashLength,
          );
        }
      }
    }
  }, [
    drawingStyles,
    activeTool,
    activeMarkerId,
    activePolygonId,
    activePolylineId,
    changePolygonColor,
    changeMarkerIcon,
    changePolylineColor,
    changePolylineWeight,
    changePolylineDash,
    markersData,
  ]);

  useEffect(() => {
    if (!currentAction?.type) {
      internalActionLock.current = null;
      return;
    }

    if (JSON.stringify(internalActionLock.current) === JSON.stringify(currentAction)) {
      return;
    }

    const action = currentAction.type;
    const p = currentAction.payload;

    if (action === 'FORCE_SELECT' && p) {
      setMarkerSelectionMode(false);
      setPolygonSelectionMode(false);
      setLineSelectionMode(false);

      handleSetSelectedMarkerData(p);

      if (p.id) {
        const targetType = p.type?.toLowerCase();

        if (targetType === 'marker' && typeof handleMarkerSelect === 'function') {
          // AMENDED: Pass the isForceSelect true flag to prevent originalStateRef from dropping rich data arrays
          handleMarkerSelect(p.id, p, true);
        } else if (targetType === 'polygon' && typeof enablePolyEditing === 'function') {
          enablePolyEditing(p.id, 'FORCE_SELECT Action');
        } else if (
          (targetType === 'line' || targetType === 'polyline') &&
          typeof enterLineEditMode === 'function'
        ) {
          enterLineEditMode(p.id, 'FORCE_SELECT Action');
        }
      }
    }

    if (action === 'CLOSE_DRAWER' && action !== 'EDIT' && action !== 'ADD_NEW') {
      forceDeselectAll('Action CLOSE_DRAWER');
    }

    if (action === 'ADD_NEW') {
      interactionLockRef.current = false;
      globalLockRef.current = null;
      setInteractionLock(false);

      triggerTooltipLockout();

      const preserve = p && p.preserveDrafts;

      if (!preserve) {
        if (typeof cancelDrawing === 'function') cancelDrawing('Action ADD_NEW');
        if (typeof cancelDrawingLine === 'function') cancelDrawingLine();
        if (typeof cancelMarker === 'function') cancelMarker();
      }

      if (activeTool === 'MARKER') {
        startNewMarker(preserve);
      } else if (activeTool === 'POLYGON' && window.google) {
        startDrawing(window.google);
      } else if (activeTool === 'LINE' && window.google) {
        startDrawingLine(window.google);
      }
    }

    if (action === 'SAVE') {
      const targetType =
        p?.type?.toLowerCase() || selectedMarker?.type?.toLowerCase() || 'marker';
      const isMarkerTab = targetType === 'marker';
      const isPolygonTab = targetType === 'polygon';
      const isLineTab = targetType === 'line' || targetType === 'polyline';

      const mId = activeMarkerId || p?.markerID || markersData.find((m) => m.isNew)?.id;
      if (mId || isPlacingMarkerRef.current || isMarkerTab) {
        const markerPayload = {
          ...p,
          id: mId,
          type: 'marker',
          isSilentAutoSave: !isMarkerTab,
        };
        delete markerPayload.polygonID;
        delete markerPayload.polylineID;
        saveMarkerEdits(markerPayload);
      }

      const pId =
        activePolygonId || p?.polygonID || polygonsData.find((poly) => poly.isNew)?.id;
      if (pId || isDrawingRef.current || isPolygonTab) {
        const polyPayload = {
          ...p,
          id: pId,
          type: 'polygon',
          isSilentAutoSave: !isPolygonTab,
        };
        delete polyPayload.markerID;
        delete polyPayload.polylineID;
        saveEdits(polyPayload);
      }

      const lId =
        activePolylineId || p?.polylineID || polylinesData.find((line) => line.isNew)?.id;
      if (lId || isDrawingLineRef.current || isLineTab) {
        const linePayload = { ...p, id: lId, type: 'line', isSilentAutoSave: !isLineTab };
        delete linePayload.markerID;
        delete linePayload.polygonID;
        saveLineEdits(linePayload);
      }
    }

    if (action === 'DELETE') {
      triggerTooltipLockout();

      const explicitId = typeof p === 'string' ? p : p?.id;
      const explicitType =
        typeof p === 'object' && p !== null ? p.type?.toLowerCase() : null;

      if (explicitId || explicitType) {
        const resolvedType =
          explicitType ||
          (polygonsData.some((poly) => poly.id === explicitId) ||
          explicitId === activePolygonId
            ? 'polygon'
            : polylinesData.some((line) => line.id === explicitId) ||
                explicitId === activePolylineId
              ? 'line'
              : selectedMarker?.type?.toLowerCase());

        if (resolvedType === 'polygon') {
          deletePolygon(explicitId || activePolygonId);
        } else if (resolvedType === 'line' || resolvedType === 'polyline') {
          deletePolyline(explicitId || activePolylineId);
        } else {
          deleteMarker(explicitId || activeMarkerId);
        }
      } else {
        const draftMarkers = markersData.filter((m) => m.isNew);
        const draftPolygons = polygonsData.filter((poly) => poly.isNew);
        const draftPolylines = polylinesData.filter((line) => line.isNew);

        let deletedSomething = false;
        if (activePolygonId) {
          deletePolygon(activePolygonId);
          deletedSomething = true;
        }
        draftPolygons.forEach((dp) => {
          if (dp.id !== activePolygonId) {
            deletePolygon(dp.id);
            deletedSomething = true;
          }
        });

        if (activePolylineId) {
          deletePolyline(activePolylineId);
          deletedSomething = true;
        }
        draftPolylines.forEach((dl) => {
          if (dl.id !== activePolylineId) {
            deletePolyline(dl.id);
            deletedSomething = true;
          }
        });

        if (activeMarkerId) {
          deleteMarker(activeMarkerId);
          deletedSomething = true;
        }
        draftMarkers.forEach((dm) => {
          if (dm.id !== activeMarkerId) {
            deleteMarker(dm.id);
            deletedSomething = true;
          }
        });

        if (!deletedSomething) {
          let targetId = selectedMarker?.id;
          let targetType = selectedMarker?.type?.toLowerCase();
          if (targetType === 'polygon') deletePolygon(targetId);
          else if (targetType === 'line' || targetType === 'polyline')
            deletePolyline(targetId);
          else deleteMarker(targetId);
        }
      }

      if (typeof cancelDrawing === 'function') cancelDrawing('DELETE');
      if (typeof cancelDrawingLine === 'function') cancelDrawingLine();
      if (typeof cancelMarker === 'function') cancelMarker();

      forceDeselectAll('Action DELETE');
    }

    if (action === 'CANCEL') {
      interactionLockRef.current = false;
      setInteractionLock(false);

      triggerTooltipLockout();

      if (isDrawingRef.current || isEditingPolygon)
        cancelEdit('GoogleMap Action CANCEL', true);
      if (isDrawingLineRef.current || isEditingPolyline)
        cancelLineEdit('GoogleMap Action CANCEL', true);
      if (isPlacingMarkerRef.current || isEditingMarker) cancelMarkerEdit(true);

      forceDeselectAll('Action CANCEL');
    }

    internalActionLock.current = currentAction;
  }, [
    currentAction,
    activeTool,
    cancelDrawing,
    cancelDrawingLine,
    cancelEdit,
    cancelLineEdit,
    cancelMarker,
    cancelMarkerEdit,
    completePolygon,
    completePolyline,
    deleteMarker,
    deletePolygon,
    deletePolyline,
    isEditingMarker,
    isEditingPolygon,
    isEditingPolyline,
    forceDeselectAll,
    saveEdits,
    saveLineEdits,
    saveMarker,
    saveMarkerEdits,
    selectedMarker,
    startDrawing,
    startDrawingLine,
    startNewMarker,
    isDrawingRef,
    isDrawingLineRef,
    isPlacingMarkerRef,
    handleMarkerSelect,
    enablePolyEditing,
    enterLineEditMode,
    handleSetSelectedMarkerData,
    setInteractionLock,
    activePolygonId,
    activePolylineId,
    activeMarkerId,
    activeMenu,
    triggerTooltipLockout,
    drawingStyles,
    defaultIcon,
    defaultIconID,
    markersData,
    polygonsData,
    polylinesData,
  ]);

  useEffect(() => {
    if (!mapContainer || mapInstance.current || !viewConfig) return;
    let isCancelled = false;
    let resizeListener = null;

    const init = async () => {
      try {
        const google = await loadGoogleMapsApi();
        if (isCancelled || !mapContainer || mapInstance.current) return;

        let attempts = 0;
        const maxAttempts = 50;

        const waitForLibrary = async () => {
          if (google && google.maps && google.maps.importLibrary) return true;
          if (attempts >= maxAttempts) return false;
          attempts++;
          await new Promise((resolve) => setTimeout(resolve, 50));
          return waitForLibrary();
        };

        const isReady = await waitForLibrary();
        if (!isReady) throw new Error('Google Maps modern importLibrary not found.');

        const { Map } = await google.maps.importLibrary('maps');
        await google.maps.importLibrary('marker');

        const containerWidth = mapContainer.clientWidth || window.innerWidth;
        const exactMinZoom = Math.log(containerWidth / 256) / Math.log(2);

        mapInstance.current = new Map(mapContainer, {
          center: viewConfig.center,
          zoom: Math.max(viewConfig.zoom || 0, exactMinZoom),
          minZoom: exactMinZoom,
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
          isFractionalZoomEnabled: true,
          draggableCursor: 'grab',
          disableDoubleClickZoom: true,
          gestureHandling: 'greedy',
        });

        resizeListener = () => {
          if (mapContainer && mapInstance.current) {
            const newWidth = mapContainer.clientWidth;
            if (newWidth > 0) {
              const newMinZoom = Math.log(newWidth / 256) / Math.log(2);
              mapInstance.current.setOptions({ minZoom: newMinZoom });
            }
          }
        };
        window.addEventListener('resize', resizeListener);

        window.__loktrMapInstance = mapInstance.current;

        if (onMapLoad) onMapLoad(mapInstance.current);

        const overlay = new google.maps.OverlayView();
        overlay.onAdd = () => {};
        overlay.onRemove = () => {};
        overlay.draw = () => {};

        google.maps.event.addListenerOnce(mapInstance.current, 'idle', () => {
          if (isCancelled) return;
          if (!overlay.getMap()) overlay.setMap(mapInstance.current);
          if (overlay.getProjection()) projectionRef.current = overlay.getProjection();

          loadPolygons(google);
          loadPolylines(google);
          loadMarkers(google);
          setIsMapLoaded(true);
        });

        mapInstance.current
          .getDiv()
          .addEventListener('contextmenu', (e) => e.stopImmediatePropagation(), true);
      } catch (err) {
        console.error('Map Initialization Failed:', err);
      }
    };
    init();
    return () => {
      isCancelled = true;
      if (resizeListener) window.removeEventListener('resize', resizeListener);
    };
  }, [mapContainer, viewConfig, loadMarkers, loadPolygons, loadPolylines, onMapLoad]);

  useEffect(() => {
    if (isMapLoaded && mapInstance.current && viewConfig) {
      mapInstance.current.setCenter(viewConfig.center);

      const containerWidth = mapContainer?.clientWidth || window.innerWidth;
      const exactMinZoom = Math.log(containerWidth / 256) / Math.log(2);
      mapInstance.current.setZoom(Math.max(viewConfig.zoom, exactMinZoom));
    }
  }, [viewConfig, isMapLoaded, mapContainer]);

  useEffect(() => {
    if (isMapLoaded && window.google && mapInstance.current) {
      const timer = setTimeout(() => {
        window.google.maps.event.trigger(mapInstance.current, 'resize');
        mapInstance.current.setCenter(mapInstance.current.getCenter());
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMapLoaded]);

  useEffect(() => {
    if (!isMapLoaded || !mapContainer) return;

    let portalDiv;
    let targetParent;

    const huntForGmStyle = setInterval(() => {
      targetParent = mapContainer.querySelector('.gm-style');
      if (targetParent && !portalDiv) {
        portalDiv = document.createElement('div');
        portalDiv.id = 'loktr-custom-portal';
        portalDiv.style.position = 'absolute';
        portalDiv.style.top = '0';
        portalDiv.style.left = '0';
        portalDiv.style.width = '100%';
        portalDiv.style.height = '100%';
        portalDiv.style.pointerEvents = 'none';
        portalDiv.style.zIndex = '999999';

        targetParent.appendChild(portalDiv);
        setPortalTarget(portalDiv);
        clearInterval(huntForGmStyle);
      }
    }, 100);

    setTimeout(() => clearInterval(huntForGmStyle), 5000);

    return () => {
      clearInterval(huntForGmStyle);
      if (targetParent && portalDiv && targetParent.contains(portalDiv)) {
        targetParent.removeChild(portalDiv);
      }
    };
  }, [isMapLoaded, mapContainer]);

  useEffect(() => {
    if (!isMapLoaded || !mapInstance.current || !window.google) return;
    const google = window.google;

    const clickListener = google.maps.event.addListener(
      mapInstance.current,
      'click',
      (event) => {
        setTimeout(() => {
          if (
            interactionLockRef.current ||
            window.__shapeClickLock ||
            window.__vertexInteractionLock
          ) {
            return;
          }

          const isLocalDrawing =
            isPlacingMarkerRef.current ||
            isDrawingRef.current ||
            isDrawingLineRef.current;

          if (hasUnsavedChangesRef.current && !isLocalDrawing) {
            if (onSelectionBlocked) onSelectionBlocked();
            return;
          }

          if (isLocalDrawing) {
            if (isPlacingMarkerRef.current) {
              const newMarker = handleMapClickForMarker(window.google, event);
              if (newMarker && setSelectedMarkerData) setSelectedMarkerData(newMarker);
              return;
            }
            if (isDrawingRef.current) {
              handleDrawClick(event.latLng);
              return;
            }
            if (isDrawingLineRef.current) {
              handleLineDrawClick(event.latLng);
              return;
            }
            return;
          }

          forceDeselectAll('Map Canvas Clicked');
          if (resetMap) resetMap();
          if (onClearSearchMarker) onClearSearchMarker();
          if (setSelectedMarkerData) setSelectedMarkerData(null);
        }, 150);
      },
    );

    const moveListener = google.maps.event.addListener(
      mapInstance.current,
      'mousemove',
      (e) => {
        if (isDrawingRef.current) handleMouseMove(e.latLng);
        if (isDrawingLineRef.current) handleLineMouseMove(e.latLng);
      },
    );

    const dblClickListener = google.maps.event.addListener(
      mapInstance.current,
      'dblclick',
      (e) => {
        if (isDrawingRef.current) {
          e.stop();
          completePolygon(window.google);
          if (changePolygonColor)
            changePolygonColor(
              drawingStylesRef.current?.polygonColor || DEFAULT_POLYGON_COLOR,
            );
        }
        if (isDrawingLineRef.current) {
          e.stop();
          completePolyline(window.google);
          if (changePolylineColor)
            changePolylineColor(
              drawingStylesRef.current?.lineColor || DEFAULT_LINE_COLOR,
            );
          if (changePolylineWeight)
            changePolylineWeight(
              drawingStylesRef.current?.lineWeight || DEFAULT_LINE_WEIGHT,
            );
        }
      },
    );

    return () => {
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(moveListener);
      google.maps.event.removeListener(dblClickListener);
    };
  }, [
    isMapLoaded,
    handleMapClickForMarker,
    handleDrawClick,
    handleLineDrawClick,
    handleMouseMove,
    handleLineMouseMove,
    completePolygon,
    completePolyline,
    forceDeselectAll,
    onSelectionBlocked,
    onClearSearchMarker,
    setSelectedMarkerData,
    currentAction,
    isDrawingLineRef,
    isDrawingRef,
    isPlacingMarkerRef,
    activeTool,
    resetMap,
    changePolygonColor,
    changePolylineColor,
    changePolylineWeight,
  ]);

  const spinnerColor =
    activeTool === 'POLYGON'
      ? drawingStyles.polygonColor || '#00B0FF'
      : activeTool === 'LINE'
        ? drawingStyles.lineColor || '#00B0FF'
        : '#00B0FF';

  const hideTooltip = isDrawMode || isCurrentlyDrawing;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        ...style,
      }}>
      <Box
        id='map'
        ref={setMapContainer}
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
      {isMapLoaded && mapInstance.current && window.google && (
        <InteractionLayer
          map={mapInstance.current}
          google={window.google}
          activeMenu={activeMenu}
          selectedMarker={selectedMarker}
        />
      )}

      {markerCounts.total > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '8px 16px',
            borderRadius: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
          {isRenderingMarkers && <CircularProgress size={14} sx={{ color: '#00B0FF' }} />}
          {markerCounts.totalInView > 0 ? (
            <Typography variant='body2' sx={{ fontSize: '14px', color: '#333' }}>
              Showing{' '}
              <Box component='span' fontWeight='bold'>
                {markerCounts.displayed}
              </Box>{' '}
              of{' '}
              <Box component='span' fontWeight='bold'>
                {markerCounts.totalInView}
              </Box>{' '}
              locations in this area
            </Typography>
          ) : (
            <Typography variant='body2' sx={{ fontSize: '14px', color: '#333' }}>
              No locations in this area
            </Typography>
          )}
        </Box>
      )}

      <MapOverlays
        isSaving={isSaving}
        spinnerColor={spinnerColor}
        showDebug={false}
        tooltip={hideTooltip || tooltipLockRef.current ? null : tooltip}
        selectedMarker={selectedMarker}
        activeTool={activeTool}
        portalTarget={portalTarget}
      />
    </Box>
  );
}
